import React, { createContext, useContext, useState } from 'react';
import { flushPendingToasts } from '../context/NotificationContext';
import { getApiUrl } from '../utils/apiConfig';

const AuthContext = createContext(undefined);

const session = {
  get:       (key)        => { try { const r = sessionStorage.getItem(key); return r ? JSON.parse(r) : null; } catch { return null; } },
  set:       (key, value) => { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch {} },
  remove:    (key)        => { try { sessionStorage.removeItem(key); } catch {} },
  getRaw:    (key)        => sessionStorage.getItem(key),
  setRaw:    (key, value) => sessionStorage.setItem(key, value),
  removeRaw: (key)        => sessionStorage.removeItem(key),
};

// Removed hardcoded DEMO_USERS to ensure backend authentication and status checks are strictly enforced.

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const saved = session.get('lw_user');
    if (saved) return saved;
    return null;
  });
  const [avatar, setAvatar] = useState(() => session.getRaw('lw_avatar') || null);

  const updateAvatar = (dataUrl) => {
    if (dataUrl) { session.setRaw('lw_avatar', dataUrl); }
    else         { session.removeRaw('lw_avatar'); }
    setAvatar(dataUrl);
  };

  const login = async (email, password, onPendingToasts) => {
    return await baseLogin({ email, password, loginMethod: 'password' }, onPendingToasts);
  };

  const loginWithKingChat = async (accessToken, onPendingToasts, kcUser) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s timeout

    try {
      const apiUrl = getApiUrl();
      console.log(`[${new Date().toLocaleTimeString()}] AuthContext: Initiating KingsChat backend auth at ${apiUrl}`);
      
      const res = await fetch(`${apiUrl}/api/auth/kingchat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          token: accessToken,
          email: (kcUser?.email && typeof kcUser.email === 'object') ? (kcUser.email.address || kcUser.email.email || null) : (kcUser?.email || null),
          firstName: (kcUser?.first_name || kcUser?.firstName || (kcUser?.name ? kcUser.name.split(' ')[0] : null)),
          lastName: (kcUser?.last_name || kcUser?.lastName || (kcUser?.name && kcUser.name.includes(' ') ? kcUser.name.split(' ').slice(1).join(' ') : null)),
          username: kcUser?.username || null,
        })
      });

      clearTimeout(timeoutId);
      console.log(`[${new Date().toLocaleTimeString()}] AuthContext: Backend responded with status ${res.status}`);

      if (!res.ok) {
        const errText = await res.text();
        console.error("Backend Auth Error:", errText);
        return errText || `KingsChat login failed (${res.status}).`;
      }

      const userData = await res.json();
      console.log("KingsChat Login Success. UserData:", userData);

      if (userData.status && userData.status !== 'active') {
        session.remove('lw_user');
        setUser(null);
        return 'Your account is pending admin approval. Please contact an administrator.';
      }

      session.set('lw_user', userData);
      setUser(userData);
      console.log("AuthContext: User state updated.");

      const pending = flushPendingToasts(userData.role);
      if (pending.length && onPendingToasts) onPendingToasts(pending, userData.role);

      return null;
    } catch (err) {
      clearTimeout(timeoutId);
      console.error("KingsChat Auth failed:", err);
      if (err.name === 'AbortError') {
        return 'Request timed out. The server is taking too long to respond. Please try again.';
      }
      return 'Connection error: Could not reach the authentication server.';
    }
  };

  const baseLogin = async (payload, onPendingToasts) => {
    const apiUrl = getApiUrl();
    const endpoint = `${apiUrl}/api/auth/login`;
    console.log(`[Auth] Executing login request to: ${endpoint}`);
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errText = await res.text();
        if (errText && errText.length < 300 && !errText.includes('<!DOCTYPE')) {
          try {
            const parsed = JSON.parse(errText);
            return parsed.message || parsed.error || errText;
          } catch {
            return errText;
          }
        }
        return `Login Failed (${res.status})`;
      }

      const userData = await res.json();
      
      if (userData.status && userData.status !== 'active') {
        session.remove('lw_user');
        setUser(null);
        return 'Your account is pending admin approval. Please contact an administrator.';
      }

      session.set('lw_user', userData);
      setUser(userData);

      const pending = flushPendingToasts(userData.role);
      if (pending.length && onPendingToasts) onPendingToasts(pending, userData.role);

      return null;
    } catch (err) {
      console.error("Login failed:", err);
      if (err.name === 'TypeError' || err.message?.includes('fetch')) {
        return `Connection error: Could not reach backend server at ${endpoint}.`;
      }
      return 'Network error occurred during login.';
    }
  };

  const logout = () => {
    session.remove('lw_user');
    session.removeRaw('lw_avatar');
    setUser(null);
    setAvatar(null);
  };

  const signup = async (name, email, password, phone) => {
    try {
      const apiUrl = getApiUrl();
      const res = await fetch(`${apiUrl}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password, phone })
      });

      if (!res.ok) {
        const errText = await res.text();
        return errText || 'Registration failed.';
      }
      return null;
    } catch (err) {
      console.error("Signup failed:", err);
      return 'Network error.';
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, loginWithKingChat, signup, logout, avatar, updateAvatar }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
